'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
interface V2Pool {
  pool_address: string;
  price0: number;
  price1: number;
  reserve0_hr: number;
  reserve1_hr: number;
  token0: string;
  token0_symbol: string;
  token1: string;
  token1_symbol: string;
  tvl_usd: number;
  fee?: number;
}
interface V2PoolsResponse {
  pools: V2Pool[];
  total_tvl: number;
}
interface V3Pool {
  creation_block: number;
  error: boolean;
  error_message?: string;
  fee: number;
  pool_address: string;
  tickSpacing: number;
  token0: string;
  token0_symbol: string;
  token1: string;
  token1_symbol: string;
  tvl_usd: number;
}
interface V3PoolsResponse {
  cumulative_tvl: number;
  pools: V3Pool[];
}
export interface ProtocolData {
  bswap_circulating_supply: number;
  bswap_marketcap: number;
  bswap_price: number;
  bsx_circulating_supply: number;
  bsx_marketcap: number;
  bsx_price: number;
  cumulative_dex_protocol_revenue: number;
  cumulative_fees_total: number;
  cumulative_fees_v2_dex: number;
  cumulative_fees_v2_total: number;
  cumulative_fees_v2_user: number;
  cumulative_fees_v3_dex: number;
  cumulative_fees_v3_total: number;
  cumulative_fees_v3_user: number;
  cumulative_staker_revenue: number;
  cumulative_user_lp_fees: number;
  cumulative_volume_total: number;
  cumulative_volume_v2: number;
  cumulative_volume_v3: number;
  current_24hr_volume_total: number;
  current_24hr_volume_v2: number;
  current_24hr_volume_v3: number;
  current_tvl_stakers: number;
  current_tvl_total: number;
  current_tvl_v2: number;
  current_tvl_v3: number;
  last_updated_timestamp_utc: string;
}
interface LoadingState {
  protocol: boolean;
  v2Pools: boolean;
  v3Pools: boolean;
}
interface ErrorState {
  protocol: string | null;
  v2Pools: string | null;
  v3Pools: string | null;
}
interface AnalyticsContextData {
  protocolData: ProtocolData | null;
  v2PoolsData: V2PoolsResponse | null;
  v3PoolsData: V3PoolsResponse | null;
  isLoading: LoadingState;
  errors: ErrorState;
  activeTabIndex: number | null;
  setActiveTabIndex: (index: number) => void;
  refreshData: (dataType?: 'protocol' | 'v2' | 'v3' | 'all') => Promise<void>;
  formatValue: (value: number) => string;
  formatFee: (fee: number) => string;
}
const AnalyticsContext = createContext<AnalyticsContextData | undefined>(undefined);
interface AnalyticsProviderProps {
  children: ReactNode;
}
export function AnalyticsProvider({
  children
}: AnalyticsProviderProps) {
  const [protocolData, setProtocolData] = useState<ProtocolData | null>(null);
  const [v2PoolsData, setV2PoolsData] = useState<V2PoolsResponse | null>(null);
  const [v3PoolsData, setV3PoolsData] = useState<V3PoolsResponse | null>(null);
  const [isLoading, setIsLoading] = useState<LoadingState>({
    protocol: false,
    v2Pools: false,
    v3Pools: false
  });
  const [errors, setErrors] = useState<ErrorState>({
    protocol: null,
    v2Pools: null,
    v3Pools: null
  });
  const [activeTabIndex, setActiveTabIndex] = useState<number | null>(null);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const savedIndex = localStorage.getItem('analyticsTabIndex');
      if (savedIndex !== null) {
        setActiveTabIndex(parseInt(savedIndex, 10));
      } else {
        setActiveTabIndex(0);
      }
    }
  }, []);
  useEffect(() => {
    if (typeof window !== 'undefined' && activeTabIndex !== null) {
      localStorage.setItem('analyticsTabIndex', activeTabIndex.toString());
    }
  }, [activeTabIndex]);
  const fetchProtocolData = async () => {
    setIsLoading(prev => ({
      ...prev,
      protocol: true
    }));
    setErrors(prev => ({
      ...prev,
      protocol: null
    }));
    try {
      const response = await fetch('/api/analytics/Protocol');
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setProtocolData(data);
    } catch (err: any) {
      console.error('Error fetching protocol data:', err);
      setErrors(prev => ({
        ...prev,
        protocol: err.message || 'Failed to fetch protocol data'
      }));
    } finally {
      setIsLoading(prev => ({
        ...prev,
        protocol: false
      }));
    }
  };
  const fetchV2PoolsData = async () => {
    setIsLoading(prev => ({
      ...prev,
      v2Pools: true
    }));
    setErrors(prev => ({
      ...prev,
      v2Pools: null
    }));
    try {
      const response = await fetch('/api/analytics/V2');
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      const correctedData = {
        ...data,
        total_tvl: data.total_tvl,
        pools: data.pools.map(pool => ({
          ...pool,
          tvl_usd: pool.tvl_usd,
          fee: 2500
        }))
      };
      setV2PoolsData(correctedData);
    } catch (err: any) {
      console.error('Error fetching V2 pools data:', err);
      setErrors(prev => ({
        ...prev,
        v2Pools: err.message || 'Failed to fetch V2 pools data'
      }));
    } finally {
      setIsLoading(prev => ({
        ...prev,
        v2Pools: false
      }));
    }
  };
  const fetchV3PoolsData = async () => {
    setIsLoading(prev => ({
      ...prev,
      v3Pools: true
    }));
    setErrors(prev => ({
      ...prev,
      v3Pools: null
    }));
    try {
      const response = await fetch('/api/analytics/V3');
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      setV3PoolsData(data);
    } catch (err: any) {
      console.error('Error fetching V3 pools data:', err);
      setErrors(prev => ({
        ...prev,
        v3Pools: err.message || 'Failed to fetch V3 pools data'
      }));
    } finally {
      setIsLoading(prev => ({
        ...prev,
        v3Pools: false
      }));
    }
  };
  const refreshData = async (dataType: 'protocol' | 'v2' | 'v3' | 'all' = 'all') => {
    if (dataType === 'protocol' || dataType === 'all') {
      await fetchProtocolData();
    }
    if (dataType === 'v2' || dataType === 'all') {
      await fetchV2PoolsData();
    }
    if (dataType === 'v3' || dataType === 'all') {
      await fetchV3PoolsData();
    }
  };
  useEffect(() => {
    fetchProtocolData();
    if (activeTabIndex === 1) {
      fetchV2PoolsData();
    } else if (activeTabIndex === 2) {
      fetchV3PoolsData();
    }
  }, [activeTabIndex]);
  const formatValue = (value: number) => {
    if (value < 0.01) return '< $0.01';
    if (value < 1000) return `$${value.toFixed(2)}`;
    if (value < 1000000) return `$${(value / 1000).toFixed(2)}K`;
    if (value < 1000000000) return `$${(value / 1000000).toFixed(2)}M`;
    return `$${(value / 1000000000).toFixed(2)}B`;
  };
  const formatFee = (fee: number) => {
    const feePercentage = fee / 10000;
    if (feePercentage < 0.1) {
      return `${feePercentage.toFixed(3)}%`;
    }
    return `${feePercentage.toFixed(2)}%`;
  };
  const contextValue: AnalyticsContextData = {
    protocolData,
    v2PoolsData,
    v3PoolsData,
    isLoading,
    errors,
    activeTabIndex,
    setActiveTabIndex,
    refreshData,
    formatValue,
    formatFee
  };
  return <AnalyticsContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="AnalyticsProvider" data-sentry-source-file="AnalyticsContext.tsx">{children}</AnalyticsContext.Provider>;
}
export function useAnalytics() {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
}